// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.101Z
// Collection: rentals/$rentalId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * A GPU rental session linking a renter to a registered GPU node. OFFCHAIN. The backend (PROJECT_VAULT_ADDRESS) manages all rental state: on start it marks the node isRented, records ratePerSecLamports (from the node's rentPricePerSecLamports) and expiresAt (startedAt + balance//ratePerSec) for lazy auto-free (no cron needed); it lazily accrues cost (accruedLamports) debiting the renter's rentalBalances ledger between settle points; on stop (explicit or when expiresAt passes / balance exhausted) it settles, pays the node owner via rentalPayouts (kind='providerSettle'), frees the node, and sets status='stopped'. ONE active rental per renter (enforced in the backend). Minimum runtime is MIN_RENTAL_SECONDS (60s) of balance to start. Native SOL only (lamports). All money math is server-side; the frontend live counter is display-only and cannot exceed the ledger balance at settle time.
 */

export interface RentalsRequest {
  renter: AddressType;
  nodeId: string;
  nodeOwner: AddressType;
  ratePerSecLamports: number | TimeOperation | IncrementOperation | TokenAmount;
  startedAt: number | TimeOperation | IncrementOperation | TokenAmount;
  lastSettleAt: number | TimeOperation | IncrementOperation | TokenAmount;
  accruedLamports: number | TimeOperation | IncrementOperation | TokenAmount;
  expiresAt: number | TimeOperation | IncrementOperation | TokenAmount;
  status: string;
  stoppedAt: number | TimeOperation | IncrementOperation | TokenAmount;
  totalPaidLamports: number | TimeOperation | IncrementOperation | TokenAmount;
  containerImage: string;
  command?: string;
  envVars?: string;
  requiredTier: string;
  matchedAt?: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface RentalsResponse {
  renter: string;
  nodeId: string;
  nodeOwner: string;
  ratePerSecLamports: number;
  startedAt: number;
  lastSettleAt: number;
  accruedLamports: number;
  expiresAt: number;
  status: string;
  stoppedAt: number;
  totalPaidLamports: number;
  containerImage: string;
  command?: string;
  envVars?: string;
  requiredTier: string;
  matchedAt?: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Rentals operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildRentals(rentalId: string, data: RentalsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rentals/${rentalId}`, document: doc };
}

/**
 * BACKEND ONLY. Caller MUST be @constants.PROJECT_VAULT_ADDRESS. On start the backend validates the renter is logged in, the node exists, is active, not already rented, the renter is not the node owner, has no other active rental, and has at least MIN_RENTAL_SECONDS (60) worth of balance; then it sets renter, nodeId, nodeOwner, ratePerSecLamports, startedAt/lastSettleAt = server time, accruedLamports=0, expiresAt, status (a plain String; backend uses values 'active'/'stopped' for the rent engine and additionally 'queued'/'matched'/'running'/'completed'/'failed' for the workload-deployment lifecycle — NO enum constraint), stoppedAt=0, totalPaidLamports=0, and the workload spec: containerImage (required, e.g. 'pytorch/pytorch:latest'), optional command, optional envVars (JSON string of key/value pairs), requiredTier (required, one of 'low'/'mid'/'high'/'datacenter'), optional matchedAt (unix seconds set when auto-matched). It also marks the node isRented=true + currentRentalId=<rentalId>. All numeric fields are required UInt (default 0) to avoid null arithmetic. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setRentals(rentalId: string, data: RentalsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rentals/${rentalId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Rentals: ${formatError(error)}`);
    return false;
  }
}

export type RentalsRequestUpdate = Partial<RentalsRequest>;

/**
 * Build a Rentals update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateRentals(rentalId: string, data: RentalsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `rentals/${rentalId}`, document: doc };
}

/**
 * BACKEND ONLY. Caller MUST be @constants.PROJECT_VAULT_ADDRESS. On stop/settle the backend recomputes cost from STORED startedAt/ratePerSecLamports and current server time (never trusting the client), pays the node owner via rentalPayouts (providerSettle), debits the renter's rentalBalances ledger, sets status ('stopped' or workload-lifecycle values like 'running'/'completed'/'failed'), stoppedAt, lastSettleAt, accruedLamports, totalPaidLamports, may set matchedAt on auto-match, and frees the node when settled. Auto-stop when balance runs out is enforced purely by this settlement cap. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateRentals(rentalId: string, data: RentalsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`rentals/${rentalId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Rentals: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read rental sessions (renter, nodeId, nodeOwner, rate, timestamps, accrued/paid amounts, status, plus the deployed workload spec: containerImage, command, envVars, requiredTier, matchedAt). Public so the marketplace and dashboards can show active/past rentals/deployments and drive the live cost counter.
   (Get Single Item)
 */
export async function getRentals(rentalId: string): Promise<RentalsResponse | null> {
  try {
    const result = await get(`rentals/${rentalId}`);
    return result as RentalsResponse | null;
  } catch (error) {
    console.error(`Error getting Rentals: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Rentals document. (
  Read Operation Details: Anyone can read rental sessions (renter, nodeId, nodeOwner, rate, timestamps, accrued/paid amounts, status, plus the deployed workload spec: containerImage, command, envVars, requiredTier, matchedAt). Public so the marketplace and dashboards can show active/past rentals/deployments and drive the live cost counter.
  )
 */
export function subscribeRentals(callback: (data: RentalsResponse | null) => void, rentalId: string): Promise<() => Promise<void>> {
  return subscribe(`rentals/${rentalId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as RentalsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Rentals at path '${`rentals/${rentalId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Rentals items from collection rentals
 
  Read Operation Details: Anyone can read rental sessions (renter, nodeId, nodeOwner, rate, timestamps, accrued/paid amounts, status, plus the deployed workload spec: containerImage, command, envVars, requiredTier, matchedAt). Public so the marketplace and dashboards can show active/past rentals/deployments and drive the live cost counter.
  
 */
export async function getManyRentals(filter: string = ""): Promise<RentalsResponse[]> {
  try {
    const results = await get("rentals", { prompt: filter });
    return Array.isArray(results) ? results as RentalsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Rentals collection at path 'rentals': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Rentals collection at rentals
 
  Read Operation Details: Anyone can read rental sessions (renter, nodeId, nodeOwner, rate, timestamps, accrued/paid amounts, status, plus the deployed workload spec: containerImage, command, envVars, requiredTier, matchedAt). Public so the marketplace and dashboards can show active/past rentals/deployments and drive the live cost counter.
  
 */
export function subscribeManyRentals(callback: (data: RentalsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("rentals", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RentalsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Rentals collection at path '${"rentals"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Rentals items from collection rentals
 
  Read Operation Details: Anyone can read rental sessions (renter, nodeId, nodeOwner, rate, timestamps, accrued/paid amounts, status, plus the deployed workload spec: containerImage, command, envVars, requiredTier, matchedAt). Public so the marketplace and dashboards can show active/past rentals/deployments and drive the live cost counter.
  
 */
export async function getAllRentals(filter: string = ""): Promise<RentalsResponse[]> {
  try {
    const results = await get("rentals", { prompt: filter });
    return Array.isArray(results) ? results as RentalsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Rentals collection at path 'rentals': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Rentals collection at rentals
 
  Read Operation Details: Anyone can read rental sessions (renter, nodeId, nodeOwner, rate, timestamps, accrued/paid amounts, status, plus the deployed workload spec: containerImage, command, envVars, requiredTier, matchedAt). Public so the marketplace and dashboards can show active/past rentals/deployments and drive the live cost counter.
  
 */
export function subscribeAllRentals(callback: (data: RentalsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("rentals", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as RentalsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Rentals collection at path '${"rentals"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Rentals items in collection rentals.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countRentals(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("rentals", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Rentals at path 'rentals': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Rentals items in collection rentals.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateRentals(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("rentals", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Rentals at path 'rentals': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: BACKEND ONLY. Caller MUST be @constants.PROJECT_VAULT_ADDRESS.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteRentals(rentalId: string): Promise<boolean> {
  try {
    const succeeded = await set(`rentals/${rentalId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Rentals: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Rentals for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteRentals(rentalId: string): DocumentOperation {
  return { path: `rentals/${rentalId}`, document: null };
}

